/** @jsx jsx */
/* eslint-disable no-use-before-define */
import { jsx } from '@emotion/core'
import React from 'react'
import { format, mediaQueries, isEmpty, domAttrSet, } from '../../twd'
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts'

export const ChartFigure = ({chartData, bondInfo}) => {
    const centerPrice = (chartData[4] || {}).price
    const maxYield = chartData.length>0
        ? Math.max(...chartData.map(({ytc,ytm}) => Math.max(Math.abs(ytc),Math.abs(ytm))).map(a => parseInt(a,10)), 0)
        : 20

    return <ResponsiveContainer width="100%" height={300}>
    <ScatterChart
        margin={{
            top: 5, 
            right: maxYield>=1000 ? 40 
                : maxYield>=100 ? 30 
                : maxYield>=10 ? 20
                : 15,
            left: 5, 
            bottom: 5,
        }}
    >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />

        <XAxis
            dataKey="x"
            domain={['auto', 'auto']}
            padding={{left:20, right:10}}
            type="number"
            scale="linear"
            tickLine={false}
            interval={0}
            tick={<CustomXAxisTick />}
            ticks={chartData.map(a => a.price)}
        />

        <YAxis
            dataKey="y"
            domain={['auto', 'auto']}
            padding={{top:10, bottom:10}}
            width={25}
            scale='linear'
            orientation='right'
            tickFormatter={formatYAxisTicks}
        />

        {!isEmpty(centerPrice) && <ReferenceLine
            x={centerPrice}
            strokeDasharray="3 3"
        />}

        <Legend
            verticalAlign='top'
            align='left'
            content={renderLegend}
        />

        <Scatter name="ytm"
            data={getData(chartData, 'ytm', '#8b06b9')}
            line
            fill="#8b06b9"
            shape={renderShape} />

        {bondInfo.isCallable && <Scatter name="ytc"
            data={getData(chartData, 'ytc', '#ef6f06')}
            line
            fill="#ef6f06"
            shape={renderShape} />}

        <Tooltip
            position={{y:25}}
            offset={1}
            content={<TooltipContent {...{chartData}} isCallable={bondInfo.isCallable} />}
            isAnimationActive={false}
            cursor={<TooltipCursor />}
            wrapperStyle={{zIndex:3}}
        />

    </ScatterChart>
    </ResponsiveContainer>
}

const getData = (data, key, color) => data
    .filter( a => !isEmpty(a[key]))
    .map( (a) => ({
        key, color, x:a['price'], y:a[key]
    }))

const renderShape = ({cx, cy, x, y, width, height, color}) => {
    let p = {
        cx, cy, x, y, width, height,
        stroke: color,
        fill: '#fff',
        r: 4,
        className:'recharts-dot'
    }
    return <circle {...p}
        onMouseOver={(e) => domAttrSet(e.target, {fill:color})}
        onMouseOut={(e) => domAttrSet(e.target, {fill:'#fff'})}
        />
}

const legends = {
    'ytm': 'Yield to Maturity',
    'ytc': 'Yield to Call',
}

const renderLegend = ({payload}) => (
    <div style={{width:'100%', textAlign:'left', marginBottom:'10px'}}>{
        payload.map((entry, index) => {
            let v = entry.value
            return <span key={index}
                style={{borderLeft:'4px solid ' + entry.color}}
                css={legendCss} >
                &nbsp; {legends[v] || v}
            </span>
        })
    }</div>
)

const legendCss = {
    [mediaQueries.notXs]: {
        paddingRight: '10px'
    },
    [mediaQueries.xs]: {
        paddingRight: '10px'
    }
}

const CustomXAxisTick = ({ x, y, stroke, fill, payload, index, }) => {

    const { value, } = payload

    const isMajor = index % 2 === 0
    const text = isMajor
        ? value.toLocaleString("en-US", {minimumFractionDigits:3, maximumFractionDigits:3})
        : ''

    return <React.Fragment>
        <line class="recharts-cartesian-axis-tick-line" width="295" height="30" x="20" y="265"
            stroke={isMajor ? "#666" : "#aaa"}
            fill="none"
            x1={x}
            y1={isMajor ? "271" : "269"}
            x2={x}
            y2="265"
        ></line>
        {isMajor && <text
            width="295"
            height="30"
            x={x}
            y={y}
            stroke={stroke}
            fill={fill}
            class="recharts-text recharts-cartesian-axis-tick-value" text-anchor="middle">
        <tspan
            x={x}
            dy="0.71em"
        >{text}</tspan>
        </text>}
    </React.Fragment>
}

const formatYAxisTicks = (x) => (x.toLocaleString("en-US", {minimumFractionDigits:2, maximumFractionDigits:2}))

const TooltipContent = ({payload, active, chartData, isCallable}) => {
    if (!active) return null

    const p = payload[0]
    const a = p && p.payload
    if (!a) return null

    const b = chartData.filter(({price:x}) => x === a.x)[0]
    if (!b) return null

    return <div className="custom-tooltip"
        style={{border:'1px solid #e4e4e4', backgroundColor:'#fff', textAlign:'left', padding:'5px'}}>
        <div>
        <span style={{paddingRight:'5px'}}>Price  <b>{format.price3(b['price'])}</b></span>
        <span style={{borderLeft:'4px solid #8b06b9', paddingLeft:'5px', paddingRight:'5px'}}>YTM  <b>{format.ytw3(b['ytm'])}</b></span>
        {isCallable && <span style={{borderLeft:'4px solid #ef6f06', paddingLeft:'5px'}}>YTC <b>{format.ytw3(b['ytc'])}</b></span>}
        </div>
    </div>
}

const TooltipCursor = ({stroke, width, height, x, y, top,}) => {

    if (!x) return null

    let args = {
        x, y, stroke, width, height,
        className: 'recharts-cross recharts-tooltip-cursor',
        pointerEvents: 'none',
        strokeDasharray: '5',
        d: `M${x},${top}v${height}`
    }
    return <path {...args} />
}


// WEBPACK FOOTER //
// ./src/calculator/calc-output/chart-figure.js